import type { BehaviorDefinition } from "./types";

import { SET_EXPECTATIONS, SURVEY, THANK_AND_REINFORCE } from "./att";
import { SHARED_BEHAVIORS } from "./shared";

/**
 * Amazon Router behavior catalog. Array order is call-flow order.
 *
 * Source: Training content's mapping sheet for the Live Amazon Router soluto profiles, all of which require every
 * behavior below. Behaviors the sheet marks Never Require for Amazon (verification, account assessment, transition
 * statement, the Guided Browsing link and verbatim enrollment steps, among others) are deliberately absent.
 *
 * Variants below keep the shared behavior id so stored criteria and the judge's labels line up across clients. The
 * judge is binary and a call clears only when every behavior passes, so a behavior the sheet marks conditional must
 * pass outright when its condition never arises - there is no N/A.
 */

// DRAFT wording, pending Training content review. Verification is Never Require for Amazon, so the greeting drops it.
const GREETING: BehaviorDefinition = {
    ...SHARED_BEHAVIORS.greeting,
    intent: "Assess whether the Expert opens the call in a way that creates a positive first impression and establishes credibility - greeting the customer warmly and giving their own name - so the customer feels looked after from the first moment rather than processed. A strong greeting shapes how the rest of the call goes.",
    failIf: [
        "Expert never greets the customer, opening straight into questions or process.",
        "Expert never gives their own name.",
        "Expert opens with an open-ended pleasantry such as 'How is your day going?' or 'How are you doing today?' rather than something specific. A kind remark of that sort is welcome later, once the customer's concern has been addressed - as in 'Despite this issue I hope you are having a nice day!' - but not as the opening.",
        "Greeting is generic or so clipped that it makes no connection - a single perfunctory line straight into a demand for information.",
    ],
    passIf: [
        "Expert opens with a warm greeting rather than moving straight to business - thanking the customer for calling, or a time-of-day greeting, for example.",
        "Expert introduces themselves by name. Indicative example: 'Thank you for calling {client}, my name is _____ and I'll be happy to help you today.' - exact wording is not required.",
        "Expert's wording builds rapport and credibility, positioning them as ready and able to help rather than issuing a bare request for information.",
    ],
};

// DRAFT wording, pending Training content review. The sheet requires this only when the customer agrees to enroll.
const ASK_TO_SEND_ENROLLMENT_LINK: BehaviorDefinition = {
    ...SHARED_BEHAVIORS.ask_to_send_enrollment_link,
    intent: "Assess whether, once the customer has agreed to enroll in {home_product}, the Expert asks to text or email a link so the customer can review plan information and complete enrollment, confirming the send-link step rather than skipping it. Applies only where the customer agreed to enroll; when the customer declined the offer or did not agree to enroll there is no link to send, and the behavior passes.",
    failIf: [
        "Customer agreed to enroll and the Expert never asks to send an enrollment or plan-review link.",
        "Customer agreed to enroll and the Expert sends a link without asking or confirming the customer's preferred contact channel.",
        "Customer agreed to enroll and the Expert discusses completing enrollment but never offers a text or email link as the next step.",
        "Customer agreed to enroll and the Expert abandons the send-link step before the link is sent.",
    ],
    passIf: [
        "If the customer declined the offer or did not agree to enroll, there is no link to send and this behavior passes; treat every other condition here as satisfied in that case.",
        "After the customer agrees to enroll, the Expert asks to send an enrollment or plan-review link by text or email, or clearly attempts the send-link step.",
        "Expert confirms or requests the contact details needed for the chosen channel when those details are not already established.",
        "Expert continues into closing or post-enrollment steps after the customer responds.",
    ],
};

// DRAFT wording, pending Training content review. The sheet requires this only as needed, so a call with no concern
// passes. The rebuttal guidance is the shared one.
const RESOLVE_CONCERN: BehaviorDefinition = {
    ...SHARED_BEHAVIORS.resolve_concern,
    intent: `${SHARED_BEHAVIORS.resolve_concern.intent} When the customer neither declines the offer nor raises a concern or objection about it, there is nothing to resolve and the behavior passes.`,
    passIf: [
        "If the customer did not decline the offer and raised no concern or objection about it, there is nothing to resolve and this behavior passes; treat every other condition here as satisfied in that case.",
        ...SHARED_BEHAVIORS.resolve_concern.passIf,
    ],
};

// DRAFT wording, pending Training content review. Thanks and reinforcement only follow an enrollment, so a call where
// the customer did not enroll passes. The rest is ATT's definition.
const THANK_AND_REINFORCE_AMAZON: BehaviorDefinition = {
    ...THANK_AND_REINFORCE,
    intent: `${THANK_AND_REINFORCE.intent} When the customer did not complete enrollment there is nothing to thank them for, and the behavior passes.`,
    passIf: [
        "If the customer did not complete enrollment, there is no thanks or reinforcement to give and this behavior passes; treat every other condition here as satisfied in that case.",
        ...THANK_AND_REINFORCE.passIf,
    ],
};

export const AMAZONROUTER_BEHAVIORS: readonly BehaviorDefinition[] = [
    // ── Serve ─────────────────────────────────────────────────────────────
    GREETING,
    SHARED_BEHAVIORS.show_compassion,
    SHARED_BEHAVIORS.provide_assurance,
    SET_EXPECTATIONS,
    SHARED_BEHAVIORS.discovery_question,
    SHARED_BEHAVIORS.ask_effective_questions,
    // ── Sell — Smart Offer ────────────────────────────────────────────────
    SHARED_BEHAVIORS.core_benefits,
    SHARED_BEHAVIORS.price,
    SHARED_BEHAVIORS.ask_for_sale,
    ASK_TO_SEND_ENROLLMENT_LINK,
    RESOLVE_CONCERN,
    THANK_AND_REINFORCE_AMAZON,
    // ── Sell — Closing ────────────────────────────────────────────────────
    SURVEY,
    SHARED_BEHAVIORS.branded_closing,
];
